/**
 * Ground-step helpers for Planting Assistant (dual path:
 * manual acres + geocoded place, or pull from map plots).
 */

/** Soil / frost stub until live survey + zone lookup land. */
export const GROUND_SOIL_STUB = Object.freeze({
  zone: '',
  series: 'Not surveyed yet',
  drain: 'Unknown',
  ph: null,
  last: '',
  first: '',
  covered: false,
});

/**
 * Fixture place for unit tests (reveal / plan / growth) that need zone + soil copy.
 * Not used as UI presets.
 */
export const GROUND_PLACE_FIXTURE = Object.freeze({
  id: 'fixture.demopolis',
  name: 'Demopolis',
  label: 'Demopolis, Alabama, United States',
  latitude: 32.5175,
  longitude: -87.8364,
  zone: '8a',
  series: 'Sumter clay',
  drain: 'Poorly drained',
  ph: 5.4,
  last: 'MAR 21',
  first: 'NOV 12',
  covered: true,
});

export function parseGroundAcres(value) {
  const n = parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function formatGroundAcresInput(n) {
  if (!Number.isFinite(n) || n <= 0) return '';
  return (Math.round(n * 10) / 10).toFixed(1);
}

export function bumpGroundAcres(value, delta) {
  const current = parseGroundAcres(value);
  if (delta < 0 && current <= 0.5) return '';
  const next = Math.round((current + delta) * 10) / 10;
  return next > 0 ? formatGroundAcresInput(next) : '';
}

export function hasGroundPlaceCoords(place) {
  if (!place || typeof place !== 'object') return false;
  const lat = Number(place.latitude);
  const lng = Number(place.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/**
 * Lat/lng for Timing weather: selected plots win (primary centroid), else geocoded place.
 * @returns {{ latitude: number, longitude: number } | null}
 */
export function resolveGroundForecastPoint({ selectedPolygons = [], draftPlace = null } = {}) {
  if (Array.isArray(selectedPolygons) && selectedPolygons.length > 0) {
    const centroid = getPlotCentroid(pickPrimaryPlot(selectedPolygons));
    if (centroid) return centroid;
  }
  if (hasGroundPlaceCoords(draftPlace)) {
    return {
      latitude: Number(draftPlace.latitude),
      longitude: Number(draftPlace.longitude),
    };
  }
  return null;
}

/**
 * Extract [lng, lat] centroid from a Ground plot (feature.raw.centroid or geojson ring average).
 */
export function getPlotCentroid(poly) {
  const feature = poly?.raw || poly;
  const centroid = feature?.centroid;
  if (Array.isArray(centroid?.coordinates) && centroid.coordinates.length >= 2) {
    const longitude = Number(centroid.coordinates[0]);
    const latitude = Number(centroid.coordinates[1]);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { latitude, longitude };
    }
  }
  const geom = poly?.geojson || feature?.geojson || feature?.geometry || null;
  const ring = geom?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  ring.forEach((pt) => {
    if (!Array.isArray(pt) || pt.length < 2) return;
    const lng = Number(pt[0]);
    const lat = Number(pt[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    sumLng += lng;
    sumLat += lat;
    count += 1;
  });
  if (!count) return null;
  return { latitude: sumLat / count, longitude: sumLng / count };
}

/** Prefer the largest plot for zone / series display. */
export function pickPrimaryPlot(plots = []) {
  if (!Array.isArray(plots) || plots.length === 0) return null;
  return plots.reduce((best, poly) => {
    if (!best) return poly;
    const bestAcres = typeof best.acreage === 'number' ? best.acreage : 0;
    const nextAcres = typeof poly.acreage === 'number' ? poly.acreage : 0;
    return nextAcres > bestAcres ? poly : best;
  }, null);
}

/**
 * Normalize a Mapbox PlaceSearch selection into the Ground place shape.
 * Live zone/soil come from lookup drafts — do not seed stub "Not surveyed yet" copy.
 */
export function buildGroundPlaceFromSelection(selected) {
  if (!hasGroundPlaceCoords(selected)) return null;
  const label = String(selected.label || selected.name || '').trim();
  if (!label) return null;
  return {
    id: selected.id || '',
    name: selected.name || label,
    label,
    latitude: Number(selected.latitude),
    longitude: Number(selected.longitude),
    city: selected.city || '',
    state: selected.state || '',
    zone: '',
    series: '',
    drain: '',
    ph: null,
    last: '',
    first: '',
    covered: false,
  };
}

/**
 * Merge Ground drafts (lookup or OVERRIDE) onto the geocoded place.
 * Plan/Reveal must read this — never Demopolis fixture defaults.
 */
export function buildConfirmedGroundPlace({
  place = null,
  zoneCode = '',
  soilSeries = '',
  soilTypeLabel = '',
  soilTypeId = '',
  drainageLabel = '',
  drainageRaw = '',
  soilPh = '',
  frostLastLabel = '',
  frostFirstLabel = '',
  soilSurveyCovered = false,
} = {}) {
  const base = place && typeof place === 'object' ? { ...place } : {};
  const phRaw = soilPh !== '' && soilPh != null ? parseFloat(soilPh) : base.ph;
  const series = String(soilSeries || soilTypeLabel || base.series || '').trim();
  const drain = String(drainageRaw || drainageLabel || base.drain || '').trim();
  return {
    ...base,
    zone: String(zoneCode || base.zone || '').trim(),
    series,
    drain,
    ph: Number.isFinite(phRaw) ? phRaw : null,
    last: String(frostLastLabel || base.last || '').trim(),
    first: String(frostFirstLabel || base.first || '').trim(),
    covered: Boolean(soilSurveyCovered || base.covered),
    soilTypeId: String(soilTypeId || base.soilTypeId || '').trim(),
  };
}

/** @deprecated Prefer buildGroundPlaceFromSelection — kept as a thin alias. */
export function resolveGroundPlace(selected) {
  return buildGroundPlaceFromSelection(selected);
}

/** Matches AreaGroup.name max_length. */
export const PLAN_NAME_MAX_LENGTH = 120;

export const PLAN_NAME_PLACEHOLDER = 'e.g. Back Ridge fields';

export function normalizePlanName(value) {
  return String(value || '').trim();
}

export function hasPlanName(value) {
  return normalizePlanName(value).length > 0;
}

/** Club map source only when the hunter is president of a club that allows area calculations. */
export function canShowGroundClubSource(clubs) {
  return Array.isArray(clubs) && clubs.length > 0;
}

export function groundMapPullLabel(mode) {
  return mode === 'club' ? 'OR PULL FROM THE CLUB MAP' : 'OR PULL FROM YOUR MAP';
}

export function groundPlotsEmptyCopy(mode) {
  return mode === 'club'
    ? 'No club fields yet. Mark a field on the club map, or enter acres and a town.'
    : 'No plots on the map yet. Enter acres and a town, or draw a plot first.';
}

/**
 * Manual path needs acres + a geocoded place.
 * Map-plot path only needs acres (filled from selected plots).
 */
export function isGroundReady(acresValue, selectedPlace, selectedPlotCount = 0) {
  if (!(parseGroundAcres(acresValue) > 0)) return false;
  if (selectedPlotCount > 0) return true;
  return hasGroundPlaceCoords(selectedPlace);
}

/** Ground continue: acres/place (or plots) plus a hunter-given plan name. */
export function isGroundStepReady(
  acresValue,
  selectedPlace,
  selectedPlotCount = 0,
  planName = '',
) {
  return isGroundReady(acresValue, selectedPlace, selectedPlotCount) && hasPlanName(planName);
}

export function groundAcresHint(selectedPlotCount) {
  if (selectedPlotCount > 0) {
    const plural = selectedPlotCount === 1 ? '' : 's';
    return `${selectedPlotCount} plot${plural} selected below — edit the number to override.`;
  }
  return 'Pace it off, guess it, or pull it from a plot you have already drawn.';
}

/** Launch-state soil coverage (matches backend SOIL_SUPPORTED_STATES for now). */
export const GROUND_SOIL_SUPPORTED_STATES = Object.freeze(['AL']);

/** Preset labels when AreaGroup options have not loaded yet. */
export const SOIL_TYPE_PRESET_LABELS = Object.freeze({
  sandy_loam: 'Sandy loam',
  loam: 'Loam',
  clay: 'Clay / heavy',
  sand: 'Sand',
  muck: 'Muck / organic',
});

const US_STATE_NAME_TO_CODE = Object.freeze({
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
});

export function normalizeUsStateCode(value) {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  if (/^[A-Z]{2}$/.test(raw)) return raw;
  return US_STATE_NAME_TO_CODE[raw] || '';
}

export function resolveSoilLookupState(place, supportedStates = GROUND_SOIL_SUPPORTED_STATES) {
  let state = normalizeUsStateCode(place?.state);
  if (!state) {
    const label = String(place?.label || place?.name || '');
    const fromLabel = label.match(/,\s*([A-Za-z][A-Za-z.\s]+?)(?:,|$)/);
    if (fromLabel) state = normalizeUsStateCode(fromLabel[1]);
  }
  if (!state) return null;
  const allowed = new Set(
    (supportedStates || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean)
  );
  return allowed.has(state) ? state : null;
}

/**
 * Map SSURGO texture / mapunit text to AreaGroup soil_type preset ids.
 * Defensive FE mirror of BE normalize_texture for muname-only payloads.
 */
export function inferSoilTypePreset(text) {
  const raw = String(text || '').trim().toLowerCase();
  if (!raw) return '';
  if (/(muck|organic|peat|histosol|aquent)/.test(raw)) return 'muck';
  if (/(sandy loam|fine sandy loam|coarse-loamy|coarse loamy|fine-loamy|fine loamy|fluvent)/.test(raw)) {
    return 'sandy_loam';
  }
  // Soil taxonomy when texcl is blank (town centroids often land on these).
  if (/(udorthent|orthent|psamment)/.test(raw)) return 'sand';
  if (/clay/.test(raw)) return 'clay';
  if (/(sand|sandy)/.test(raw)) return 'sand';
  if (/loam/.test(raw)) return 'loam';
  return '';
}

export function soilTypePresetLabel(presetId) {
  const id = String(presetId || '').trim();
  return SOIL_TYPE_PRESET_LABELS[id] || '';
}

/** Mapunit symbols / placeholders are not human soil-type labels. */
export function isGenericSoilSeriesLabel(value) {
  const series = String(value || '').trim();
  if (!series) return true;
  if (/^surveyed unit$/i.test(series)) return true;
  if (/^not surveyed yet$/i.test(series)) return true;
  // Known preset labels are real soil types (including short ones like "Loam" / "Sand").
  const knownLabels = new Set(Object.values(SOIL_TYPE_PRESET_LABELS).map((l) => l.toLowerCase()));
  if (knownLabels.has(series.toLowerCase())) return false;
  // SSURGO musym codes are short alphanumeric (Ud, BnG, DcB).
  if (/^[A-Za-z0-9]{1,6}$/.test(series)) return true;
  return false;
}

/**
 * Prefer real series name, then soil-type preset label. Never show stub / musym junk.
 */
export function resolveSoilTileLabel({
  draftSoilSeries = '',
  draftSoilTypeLabel = '',
  draftSoilTypeId = '',
  placeSeries = '',
  loading = false,
} = {}) {
  const typeLabel = String(draftSoilTypeLabel || '').trim()
    || soilTypePresetLabel(draftSoilTypeId);
  const series = String(draftSoilSeries || '').trim();
  if (series && !isGenericSoilSeriesLabel(series)) return series;
  if (typeLabel) return typeLabel;
  const place = String(placeSeries || '').trim();
  if (place && !isGenericSoilSeriesLabel(place)) return place;
  if (loading) return 'Looking up…';
  return '';
}

/**
 * Normalize /soil/lookup/ payload into Ground draft soil fields.
 * Prefers SSURGO map-unit name + drainage class for Field Edition readout.
 * When drainage_class is missing/Unknown, fall back to normalized preset label
 * (often derived from hydrologic group).
 * Prefer texture_class_normalized; fall back to inferring from texture_class / mu_name
 * so series-only AL rows still fill soil_type.
 * AL launch FGB has no muname — show texture preset label instead of "Surveyed unit".
 */
export function soilSurveyFromLookup(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const attrs = payload.attributes || {};
  const series = String(payload.mu_name || '').trim();
  const rawDrainage = String(attrs.drainage_class || '').trim();
  const drainageLabel = String(attrs.drainage_class_label || '').trim();
  const drainage = rawDrainage && rawDrainage.toLowerCase() !== 'unknown'
    ? rawDrainage
    : (drainageLabel || rawDrainage || '');
  const phRaw = attrs.ph;
  const ph = phRaw !== '' && phRaw != null ? Number(phRaw) : null;
  const hasAny = !!(series || drainage || Number.isFinite(ph) || attrs.texture_class_normalized || attrs.texture_class);
  if (!hasAny) return null;
  const textureId = String(attrs.texture_class_normalized || '').trim()
    || inferSoilTypePreset(attrs.texture_class)
    || inferSoilTypePreset(series);
  const textureLabel = String(attrs.texture_class_label || '').trim()
    || soilTypePresetLabel(textureId);
  const displaySeries = (!isGenericSoilSeriesLabel(series) && series)
    || textureLabel
    || soilTypePresetLabel(textureId)
    || '';
  return {
    series: displaySeries,
    drainage: drainage || 'Unknown',
    ph: Number.isFinite(ph) ? ph : null,
    textureId,
    textureLabel: textureLabel || displaySeries,
    drainageId: attrs.drainage_class_normalized || '',
    drainageSource: attrs.drainage_class_source || '',
    covered: true,
  };
}

/**
 * Build WHAT WE READ tiles. Prefer live draft zone/soil when present;
 * otherwise use place stub fields.
 */
export function buildGroundReadout({
  acresValue,
  place,
  selectedPlotCount = 0,
  draftZoneCode = '',
  draftSoilTypeLabel = '',
  draftSoilTypeId = '',
  draftSoilDrainageLabel = '',
  draftSoilPh = '',
  draftSoilSeries = '',
  draftSoilDrainageRaw = '',
  draftFrostLastLabel = '',
  draftFrostFirstLabel = '',
  soilSurveyCovered = null,
  soilLookupLoading = false,
} = {}) {
  const acres = parseGroundAcres(acresValue);
  const acresLabel = acres > 0 ? formatGroundAcresInput(acres) : '';
  const zone = draftZoneCode
    ? String(draftZoneCode).toUpperCase()
    : place?.zone || '';
  const soil = resolveSoilTileLabel({
    draftSoilSeries,
    draftSoilTypeLabel,
    draftSoilTypeId,
    placeSeries: place?.series,
    loading: soilLookupLoading,
  });
  const drain = draftSoilDrainageRaw || draftSoilDrainageLabel || place?.drain || '';
  const phRaw = draftSoilPh !== '' && draftSoilPh != null
    ? parseFloat(draftSoilPh)
    : place?.ph;
  const ph = Number.isFinite(phRaw) ? phRaw : null;
  const frostLast = String(draftFrostLastLabel || '').trim() || place?.last || '';
  const frostFirst = String(draftFrostFirstLabel || '').trim() || place?.first || '';
  const frost = frostLast && frostFirst
    ? `${frostLast} – ${frostFirst}`
    : (frostLast || frostFirst || '');
  const resolved = acres > 0 && (!!place || selectedPlotCount > 0);
  const covered = soilSurveyCovered == null ? !!place?.covered : !!soilSurveyCovered;

  if (!resolved) {
    return {
      readState: 'WAITING',
      sourceLine: 'USDA PHZM 2023 · SOIL SURVEY NOT AVAILABLE',
      showCoverageNote: false,
      coverageNote: '',
      tiles: [
        { label: 'ACRES', value: acresLabel || '—', size: 'lg', tone: 'muted' },
        { label: 'USDA ZONE', value: '—', size: 'lg', tone: 'muted' },
        { label: 'SOIL', value: 'Waiting on a town or plot', size: 'md', tone: 'muted' },
        { label: 'DRAINAGE', value: '—', size: 'md', tone: 'muted' },
        { label: 'pH', value: '—', size: 'lg', tone: 'muted' },
        { label: 'FROST WINDOW', value: '—', size: 'md', tone: 'muted' },
      ],
    };
  }

  return {
    readState: 'RESOLVED',
    sourceLine: covered
      ? 'USDA PHZM 2023 · SSURGO SOIL SURVEY (AL)'
      : 'USDA PHZM 2023 · SOIL SURVEY NOT AVAILABLE',
    showCoverageNote: !covered,
    coverageNote:
      'Detailed soil is live in Alabama today. Mississippi, Tennessee, Georgia, Kentucky, Louisiana, Arkansas and the Carolinas are next. Until then we use your hardiness zone and two questions about how the ground behaves after rain — the recommendation gets a wider margin, and we will tell you where that margin matters.',
    tiles: [
      { label: 'ACRES', value: acresLabel, size: 'lg', tone: 'bright' },
      { label: 'USDA ZONE', value: zone || '—', size: 'lg', tone: 'accent' },
      { label: 'SOIL', value: soil || '—', size: 'md', tone: 'bright' },
      { label: 'DRAINAGE', value: drain || '—', size: 'md', tone: 'bright' },
      {
        label: 'pH',
        value: ph != null ? ph.toFixed(1) : 'TEST IT',
        size: 'lg',
        tone: ph != null ? 'bright' : 'warn',
      },
      { label: 'FROST WINDOW', value: frost || '—', size: 'md', tone: 'bright' },
    ],
  };
}

export const GROUND_NEXT_UP = Object.freeze([
  { num: '1', text: 'Five questions about what the plot is for and what you are working it with.' },
  { num: '2', text: "We read your zone, soil and this year's rainfall, and show you what it means." },
  { num: '3', text: 'You get a blend, a lime and fertilizer rate, a planting window and a growth timeline.' },
]);

export function normalizeUsdaZoneCode(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Plan note: keep any user text, replace prior Blend/Season/Town suffixes
 * so re-saving an existing plan does not stack duplicates.
 */
export function buildPlantingPlanNote({
  note = '',
  recName = '',
  seasonKey = '',
  placeLabel = '',
} = {}) {
  const structured = [
    recName ? `Blend: ${recName}` : '',
    seasonKey ? `Season: ${seasonKey}` : '',
    placeLabel ? `Town: ${placeLabel}` : '',
  ].filter(Boolean);
  const userBit = String(note || '')
    .split(' · ')
    .map((part) => part.trim())
    .filter((part) => part && !/^(Blend:|Season:|Town:)/i.test(part))
    .join(' · ');
  return [userBit, ...structured].filter(Boolean).join(' · ');
}

/**
 * Soil + zone fields for AreaGroup create/update.
 * Persist Ground lookup zones (auto) as well as manual overrides so planting
 * windows can be derived server-side.
 */
export function buildAreaGroupMetadataPayload({
  zoneSource = '',
  zoneCode = '',
  soilType = '',
  soilDrainage = '',
  soilPh = null,
  soilNotes = '',
  frostSource = '',
  frostLastLabel = '',
  frostFirstLabel = '',
} = {}) {
  const payload = {
    soil_type: soilType || '',
    soil_drainage: soilDrainage || '',
    soil_notes: soilNotes || '',
  };
  if (soilPh === '' || soilPh === null || soilPh === undefined) {
    payload.soil_ph = null;
  } else {
    const parsed = parseFloat(soilPh);
    payload.soil_ph = Number.isNaN(parsed) ? null : Math.min(14, Math.max(0, parsed));
  }

  const code = normalizeUsdaZoneCode(zoneCode);
  const source = String(zoneSource || '').trim().toLowerCase();
  if (source === 'manual') {
    payload.zone_source = 'manual';
    payload.usda_zone_code = code;
  } else if (code) {
    payload.zone_source = source || 'auto';
    payload.usda_zone_code = code;
  }

  const frostSrc = String(frostSource || '').trim().toLowerCase();
  const lastLabel = String(frostLastLabel || '').trim();
  const firstLabel = String(frostFirstLabel || '').trim();
  if (frostSrc === 'manual' || lastLabel || firstLabel) {
    payload.frost_source = frostSrc === 'manual' ? 'manual' : (frostSrc || 'auto');
    payload.last_frost_label = lastLabel;
    payload.first_frost_label = firstLabel;
  }
  return payload;
}
